/**
 * useRealtimeSync — branche le socket temps-réel sur les stores Zustand.
 *
 * Monté une seule fois au niveau de l'app (après authentification).
 * - Initialise le socket.
 * - Applique les events entrants en patchant les stores PAR ID (pas de refetch lourd).
 * - Anti-écho géré côté serveur (le socket auteur est exclu via x-socket-id), donc ici
 *   on applique tout ce qu'on reçoit sans risque de double-update.
 * - À la reconnexion : refetch ciblé pour rattraper les events manqués pendant la coupure.
 *
 * Couverture :
 *   · Missions : mission:created / updated / archived / unarchived (patch missionStore par id)
 *   · Mission sous-entités : mission_block:changed / mission_step:changed / mission_note:changed
 *     → refetch ciblé de la mission concernée si elle est ouverte/chargée
 *   · Instagram : instagram_post:created / updated / deleted / bulk (patch instagramStore si contrat courant)
 *                 instagram_contract:created / updated / deleted
 */

import { useEffect } from 'react';
import { initSocket, getSocket } from '../utils/socket';
import { useMissionStore } from '../store/missionStore';
import { useInstagramStore } from '../store/instagramStore';
import { useClientDashboardStore } from '../store/clientDashboardStore';
import { useShootingStore, invalidateShootingsCache } from '../store/shootingStore';
import { Mission } from '../types/missions';
import { logger } from '../utils/logger';

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let t: ReturnType<typeof setTimeout> | null = null;
  return ((...args: any[]) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function useRealtimeSync(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return;

    const socket = initSocket();

    // ───────────────── MISSIONS ─────────────────
    const refreshDashboardSoon = debounce(() => {
      try {
        useMissionStore.setState({ _dashboardFetchedAt: null });
        useMissionStore.getState().fetchDashboard?.();
      } catch { /* noop */ }
    }, 800);

    const upsertMission = (mission: Mission) => {
      if (!mission?.id) return;
      useMissionStore.setState((state) => {
        const exists = state.missions.some((m) => m.id === mission.id);
        const missions = exists
          ? state.missions.map((m) => (m.id === mission.id ? { ...m, ...mission } : m))
          : [mission, ...state.missions];
        return { missions };
      });
      useMissionStore.setState((state) =>
        state.currentMission?.id === mission.id
          ? { currentMission: { ...state.currentMission, ...mission } }
          : {}
      );
      refreshDashboardSoon();
    };

    const removeMission = (id: string) => {
      if (!id) return;
      useMissionStore.setState((state) => ({ missions: state.missions.filter((m) => m.id !== id) }));
      refreshDashboardSoon();
    };

    // Refetch ciblé d'une mission (sous-entité changée : step/note/block)
    const refetchMissionSoon = debounce((missionId: string) => {
      if (!missionId) return;
      try {
        const st = useMissionStore.getState();
        // Si la mission est ouverte dans le drawer → refetch détaillé
        if (st.currentMission?.id === missionId && st.fetchMissionById) {
          st.fetchMissionById(missionId);
        }
        // Et on rafraîchit la liste pour les compteurs (steps count, etc.)
        st.fetchMissions?.();
      } catch { /* noop */ }
    }, 500);

    const onMissionCreated = (d: { mission: Mission }) => { if (d?.mission) upsertMission(d.mission); };
    const onMissionUpdated = (d: { mission: Mission }) => { if (d?.mission) upsertMission(d.mission); };
    const onMissionArchived = (d: { id: string }) => { if (d?.id) removeMission(d.id); };
    const onMissionUnarchived = (d: { mission: Mission }) => { if (d?.mission) upsertMission(d.mission); };
    const onMissionSubChanged = (d: { missionId?: string }) => { if (d?.missionId) refetchMissionSoon(d.missionId); };

    socket.on('mission:created', onMissionCreated);
    socket.on('mission:updated', onMissionUpdated);
    socket.on('mission:archived', onMissionArchived);
    socket.on('mission:unarchived', onMissionUnarchived);
    socket.on('mission_block:changed', onMissionSubChanged);
    socket.on('mission_step:changed', onMissionSubChanged);
    socket.on('mission_note:changed', onMissionSubChanged);

    // ───────────────── INSTAGRAM ─────────────────
    const currentContractId = () => {
      try { return useInstagramStore.getState().currentContract?.id; } catch { return undefined; }
    };

    const upsertPost = (post: any) => {
      if (!post?.id) return;
      // Règle : on met TOUJOURS à jour un post DÉJÀ présent dans le store (peu importe la vue —
      // TodayView/accueil affiche des posts multi-contrats sans "contrat courant"). On n'INSÈRE
      // un post inconnu que s'il appartient au contrat actuellement ouvert (évite de polluer).
      useInstagramStore.setState((state: any) => {
        const exists = state.posts.some((p: any) => p.id === post.id);
        if (exists) {
          return { posts: state.posts.map((p: any) => (p.id === post.id ? { ...p, ...post } : p)) };
        }
        if (post.contractId && post.contractId === currentContractId()) {
          return { posts: [post, ...state.posts] };
        }
        return {};
      });
    };

    // Suppression : toujours retirer par id (peu importe la vue)
    const removePost = (id: string) => {
      if (!id) return;
      useInstagramStore.setState((state: any) => ({ posts: state.posts.filter((p: any) => p.id !== id) }));
    };

    // Refetch : si on est sur un contrat précis → ses posts ; sinon (vue multi-contrats type accueil) → tout
    const refetchPostsSoon = debounce((contractId?: string) => {
      try {
        const store = useInstagramStore.getState();
        const cur = currentContractId();
        if (cur && (!contractId || contractId === cur)) {
          store.fetchPosts?.(cur);
        } else {
          store.fetchAllPosts?.();
        }
      } catch { /* noop */ }
    }, 600);

    const onPostCreated = (d: { post?: any }) => { if (d?.post) upsertPost(d.post); };
    const onPostUpdated = (d: { post?: any; id?: string; contractId?: string }) => {
      if (d?.post) upsertPost(d.post);
      else if (d?.contractId) refetchPostsSoon(d.contractId);
    };
    const onPostDeleted = (d: { id?: string; contractId?: string }) => { if (d?.id) removePost(d.id); };
    const onPostBulk = (d: { contractId?: string }) => refetchPostsSoon(d?.contractId);

    socket.on('instagram_post:created', onPostCreated);
    socket.on('instagram_post:updated', onPostUpdated);
    socket.on('instagram_post:deleted', onPostDeleted);
    socket.on('instagram_post:bulk', onPostBulk);

    const upsertContract = (contract: any) => {
      if (!contract?.id) return;
      useInstagramStore.setState((state: any) => {
        const exists = state.contracts.some((c: any) => c.id === contract.id);
        const contracts = exists
          ? state.contracts.map((c: any) => (c.id === contract.id ? { ...c, ...contract } : c))
          : [...state.contracts, contract];
        return { contracts };
      });
      useInstagramStore.setState((state: any) =>
        state.currentContract?.id === contract.id
          ? { currentContract: { ...state.currentContract, ...contract } }
          : {}
      );
    };

    const onContractChanged = (d: { contract?: any }) => { if (d?.contract) upsertContract(d.contract); };
    socket.on('instagram_contract:created', onContractChanged);
    socket.on('instagram_contract:updated', onContractChanged);

    // Contrat supprimé → retire le contrat + ses posts + reset currentContract si c'était lui
    const onContractDeleted = (d: { id?: string }) => {
      if (!d?.id) return;
      useInstagramStore.setState((state: any) => ({
        contracts: state.contracts.filter((c: any) => c.id !== d.id),
        posts: state.posts.filter((p: any) => p.contractId !== d.id),
        currentContract: state.currentContract?.id === d.id ? null : state.currentContract,
      }));
    };
    socket.on('instagram_contract:deleted', onContractDeleted);

    // Profil IG mis à jour (scrape/connexion) → refetch posts/contrat affiché
    const onProfileUpdated = (d: { contractId?: string }) => { if (d?.contractId) refetchPostsSoon(d.contractId); };
    socket.on('instagram_profile:updated', onProfileUpdated);

    // Commentaire ajouté/supprimé sur un post → refetch les posts du contrat affiché
    const onPostCommentChanged = (d: { contractId?: string }) => { if (d?.contractId) refetchPostsSoon(d.contractId); };
    socket.on('instagram_post:changed', onPostCommentChanged);

    // ───────────────── SHOOTINGS ─────────────────
    const refetchShootingsSoon = debounce((contractId?: string) => {
      try {
        const cid = contractId || currentContractId();
        if (cid && cid === currentContractId()) {
          invalidateShootingsCache(); // event temps réel → on force la fraîcheur (sinon le cache 30s masquerait l'update)
          useShootingStore.getState().fetchShootings?.(cid);
        }
      } catch { /* noop */ }
    }, 500);
    const onShootingChanged = (d: { shooting?: any; contractId?: string }) => {
      refetchShootingsSoon(d?.shooting?.contractId || d?.contractId);
    };
    socket.on('shooting:created', onShootingChanged);
    socket.on('shooting:updated', onShootingChanged);
    socket.on('shooting:deleted', onShootingChanged);

    // ───────────────── CLIENTS (sidebar) ─────────────────
    const refreshSidebarSoon = debounce(() => {
      try { useClientDashboardStore.getState().fetchSidebar?.(); } catch { /* noop */ }
    }, 600);
    const onClientChanged = () => refreshSidebarSoon();
    socket.on('client:updated', onClientChanged);

    // ───────────────── RECONNEXION / RATTRAPAGE ─────────────────
    // Rattrape les events manqués pendant une coupure (mobile en arrière-plan, perte réseau).
    // IMPORTANT : sur mobile, le réveil de l'app produit souvent un 'connect' frais (pas 'reconnect').
    // On rattrape donc sur les DEUX, en ignorant le tout premier connect (déjà couvert par le mount).
    const catchUp = debounce(() => {
      logger.log('[realtime] rattrapage refetch');
      try {
        useMissionStore.setState({ _missionsFetchedAt: null, _dashboardFetchedAt: null });
        useMissionStore.getState().fetchMissions?.();
        useMissionStore.getState().fetchDashboard?.();
        const cid = currentContractId();
        const store = useInstagramStore.getState();
        if (cid) store.fetchPosts?.(cid);
        else store.fetchAllPosts?.(); // vue accueil multi-contrats
      } catch { /* noop */ }
    }, 400);

    let firstConnect = true;
    const onConnect = () => {
      if (firstConnect) { firstConnect = false; return; } // mount initial déjà chargé
      catchUp();
    };
    const onReconnect = () => catchUp();
    socket.on('connect', onConnect);
    socket.io.on('reconnect', onReconnect);

    return () => {
      const s = getSocket();
      if (s) {
        s.off('mission:created', onMissionCreated);
        s.off('mission:updated', onMissionUpdated);
        s.off('mission:archived', onMissionArchived);
        s.off('mission:unarchived', onMissionUnarchived);
        s.off('mission_block:changed', onMissionSubChanged);
        s.off('mission_step:changed', onMissionSubChanged);
        s.off('mission_note:changed', onMissionSubChanged);
        s.off('instagram_post:created', onPostCreated);
        s.off('instagram_post:updated', onPostUpdated);
        s.off('instagram_post:deleted', onPostDeleted);
        s.off('instagram_post:bulk', onPostBulk);
        s.off('instagram_contract:created', onContractChanged);
        s.off('instagram_contract:updated', onContractChanged);
        s.off('instagram_contract:deleted', onContractDeleted);
        s.off('instagram_profile:updated', onProfileUpdated);
        s.off('instagram_post:changed', onPostCommentChanged);
        s.off('shooting:created', onShootingChanged);
        s.off('shooting:updated', onShootingChanged);
        s.off('shooting:deleted', onShootingChanged);
        s.off('client:updated', onClientChanged);
        s.off('connect', onConnect);
        s.io.off('reconnect', onReconnect);
      }
    };
  }, [enabled]);
}
